import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, ArrowUpRight, Loader2 } from 'lucide-react';
import { getPlayerLeaderboard } from '../api/client';
import { siteName, siteParam, useSites } from './SiteFilter';
import { formatAmount } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { InfoTip } from './InfoTip';

const METRIC_INFO: Record<string, string> = { ggr: 'ggr', net_cash: 'netCash', total_deposits: 'deposits', total_withdrawals: 'withdrawals' };

interface LeaderboardPlayer {
  player_id: string;
  username?: string;
  site_id: number;
  ggr: number;
  casino_ggr?: number;
  sport_ggr?: number;
  total_deposits: number;
  total_withdrawals: number;
  net_cash: number;
}

type Metric = 'ggr' | 'net_cash' | 'total_deposits' | 'total_withdrawals';

interface Props {
  siteIds: number[];
  dateFrom: string;
  dateTo: string;
}

const TOP_N = 10;

/**
 * Player performance overview shown on the Active Players report. Surfaces the
 * top and bottom performers (by net cash, deposits or withdrawals) so an
 * operator can spot their most valuable — and most costly — players at a glance,
 * then drill into the single-player detail report.
 */
export const PlayerLeaderboard: React.FC<Props> = ({ siteIds, dateFrom, dateTo }) => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [rows, setRows] = useState<LeaderboardPlayer[]>([]);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState<Metric>('ggr');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPlayerLeaderboard({ date_from: dateFrom, date_to: dateTo, site_ids: siteParam(allSites, siteIds), limit: 500 })
      .then((res) => { if (!cancelled) setRows(res.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, siteIds.join(',')]);

  const { top, bottom } = useMemo(() => {
    const sorted = [...rows].sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
    return {
      top: sorted.slice(0, TOP_N),
      bottom: sorted.slice(-TOP_N).reverse(),
    };
  }, [rows, metric]);

  const metricLabel = (m: Metric) =>
    m === 'ggr' ? t('playerLeaderboard.ggr', 'GGR')
      : m === 'net_cash' ? t('playerLeaderboard.netCash', 'Net cash')
        : m === 'total_deposits' ? t('playerLeaderboard.deposits', 'Deposits')
          : t('playerLeaderboard.withdrawals', 'Withdrawals');

  const renderTable = (list: LeaderboardPlayer[], kind: 'top' | 'bottom') => (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        {kind === 'top'
          ? <TrendingUp className="h-4 w-4 text-emerald-500" />
          : <TrendingDown className="h-4 w-4 text-red-500" />}
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          {kind === 'top' ? t('playerLeaderboard.top', 'Top performers') : t('playerLeaderboard.bottom', 'Bottom performers')}
        </h3>
      </div>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 dark:bg-slate-800/60">
          <tr>
            <th className="px-4 py-2 font-medium">#</th>
            <th className="px-4 py-2 font-medium"><InfoTip tip={metricTip('player')}>{t('playerLeaderboard.player', 'Player')}</InfoTip></th>
            <th className="px-4 py-2 text-right font-medium"><InfoTip tip={metricTip(METRIC_INFO[metric])} className="justify-end">{metricLabel(metric)}</InfoTip></th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
          {list.length === 0 ? (
            <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">{t('playerLeaderboard.empty', 'No player data')}</td></tr>
          ) : list.map((p, i) => {
            const value = p[metric] || 0;
            return (
              <tr key={p.player_id} className="group hover:bg-slate-50 dark:hover:bg-slate-700/30">
                <td className="px-4 py-2 text-slate-400">{i + 1}</td>
                <td className="px-4 py-2">
                  <Link
                    to={`/reports/players/detail?player_id=${encodeURIComponent(p.player_id)}&site_id=${p.site_id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {p.username || p.player_id}
                  </Link>
                  <span className="ml-2 text-xs text-slate-400" title={`ID ${p.site_id}`}>{siteName(allSites, p.site_id)}</span>
                </td>
                <td className={`px-4 py-2 text-right font-mono ${value < 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
                  €{formatAmount(value)}
                </td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to={`/reports/players/detail?player_id=${encodeURIComponent(p.player_id)}&site_id=${p.site_id}`}
                    className="inline-flex items-center text-slate-400 opacity-0 transition-opacity group-hover:opacity-100 hover:text-accent"
                    aria-label={t('playerLeaderboard.viewDetail', 'View detail')}
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('playerLeaderboard.title', 'Player Performance')}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('playerLeaderboard.subtitle', 'Top and bottom performers — click a player for the full detail report')}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500 dark:text-slate-400">{t('playerLeaderboard.metric', 'Rank by')}</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
          >
            <option value="ggr">{t('playerLeaderboard.ggr', 'GGR')}</option>
            <option value="net_cash">{t('playerLeaderboard.netCash', 'Net cash')}</option>
            <option value="total_deposits">{t('playerLeaderboard.deposits', 'Deposits')}</option>
            <option value="total_withdrawals">{t('playerLeaderboard.withdrawals', 'Withdrawals')}</option>
          </select>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('playerLeaderboard.loading', 'Loading…')}
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {renderTable(top, 'top')}
          {renderTable(bottom, 'bottom')}
        </div>
      )}
    </div>
  );
};
